import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import Tools from "../components/Tools";
import Faq from "../components/Faq";
import InBlog from "../components/InBlog";
import ChatButton from "../components/ChatButton";

const STOP_WORDS = new Set([
  "the", "and", "but", "you", "are", "for", "that", "with", "was", "this", "have",
  "not", "from", "they", "she", "his", "her", "its", "had", "has", "were", "all",
  "there", "their", "what", "which", "when", "where", "how", "will", "shall",
  "can", "could", "would", "should", "may", "might", "just", "more", "some",
  "been", "any", "each", "very", "into", "then", "than", "them", "too", "such",
  "out", "our", "about", "over", "only", "off", "also", "if", "because", "while",
  "under", "again", "most", "own", "same", "do", "does", "did", "so", "no", "nor",
  "up", "down", "in", "on", "to", "of", "is", "it", "as", "at", "an", "be", "by",
  "or", "a", "i", "we", "you", "your", "me", "my", "us", "one", "two", "three",
  "get", "go", "come", "see", "know", "take", "use", "make", "way", "time", "work",
  "part", "first", "last", "good", "great", "new", "old", "right", "left", "here",
  "now", "today", "yesterday", "tomorrow", "yes", "true", "false", "big", "small"
]);

const extractMainContent = (doc) => {
  const elementsToRemove = doc.querySelectorAll(
    "script, style, nav, header, footer, aside, form, noscript, iframe, .menu, .navigation, .sidebar, .ads, .advertisement, .social, .share, .comment"
  );
  elementsToRemove.forEach((el) => el.remove());

  const selectors = [
    "main",
    "article",
    '[role="main"]',
    ".content",
    ".main-content",
    "#content",
    ".post-content",
    ".entry-content",
    ".article-content",
  ];

  let mainSection = null;
  for (const selector of selectors) {
    mainSection = doc.querySelector(selector);
    if (mainSection) break;
  }

  if (!mainSection) {
    mainSection = doc.body;
  }

  return mainSection ? mainSection.innerText || "" : "";
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const analyzeWritingStyle = (text) => {
  const cleaned = text
    .replace(/\s+/g, " ")
    .replace(/\u00A0/g, " ")
    .trim();

  const sentences = cleaned
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.split(/\s+/).filter(Boolean).length >= 3);

  const rawWords = cleaned
    .toLowerCase()
    .replace(/[^a-z0-9\s']/g, " ")
    .split(/\s+/)
    .map((word) => word.replace(/'+/g, "").trim())
    .filter(Boolean);

  const totalWords = rawWords.length;

  if (totalWords < 80) {
    throw new Error("We need a bit more written content to review. Try a longer article or page.");
  }

  const sentenceLengths = sentences.length
    ? sentences.map((s) => s.split(/\s+/).filter(Boolean).length)
    : [totalWords];

  const averageSentenceLength = sentenceLengths.reduce((sum, len) => sum + len, 0) / sentenceLengths.length;
  const variance = sentenceLengths.reduce((sum, len) => sum + Math.pow(len - averageSentenceLength, 2), 0) / sentenceLengths.length;
  const sentenceVariation = averageSentenceLength > 0 ? Math.sqrt(variance) / averageSentenceLength : 0;

  const uniqueWords = new Set(rawWords).size;
  const lexicalRichness = totalWords ? uniqueWords / totalWords : 0;

  const stopWordCount = rawWords.reduce((count, word) => count + (STOP_WORDS.has(word) ? 1 : 0), 0);
  const stopWordRatio = totalWords ? stopWordCount / totalWords : 0;

  const longWordCount = rawWords.filter((word) => word.length >= 8).length;
  const longWordRatio = totalWords ? longWordCount / totalWords : 0;

  const frequencyMap = rawWords.reduce((map, word) => {
    map[word] = (map[word] || 0) + 1;
    return map;
  }, {});
  const topWordFrequency = Object.values(frequencyMap).reduce((max, current) => Math.max(max, current), 0);
  const topWordShare = totalWords ? topWordFrequency / totalWords : 0;

  const bigrams = [];
  for (let i = 0; i < rawWords.length - 1; i += 1) {
    bigrams.push(`${rawWords[i]} ${rawWords[i + 1]}`);
  }
  const bigramFrequency = bigrams.reduce((map, phrase) => {
    map[phrase] = (map[phrase] || 0) + 1;
    return map;
  }, {});
  const maxBigramFrequency = Object.values(bigramFrequency).reduce((max, current) => Math.max(max, current), 0);
  const repetitivePhraseRatio = bigrams.length ? maxBigramFrequency / bigrams.length : 0;

  let score = 50;
  const insights = [];

  if (lexicalRichness < 0.45) {
    score += 12;
    insights.push("Limited vocabulary variety often appears in machine-written copy.");
  } else if (lexicalRichness > 0.65) {
    score -= 8;
    insights.push("High vocabulary variety points toward a more human writing style.");
  }

  if (sentenceVariation < 0.25 && sentences.length > 3) {
    score += 15;
    insights.push("Very consistent sentence length suggests templated or automated writing.");
  } else if (sentenceVariation > 0.55) {
    score -= 5;
    insights.push("Varied sentence length is typical in human writing.");
  }

  if (averageSentenceLength > 28 || averageSentenceLength < 9) {
    score += 8;
    insights.push("Unusually short or long sentences compared to web copy norms.");
  } else if (averageSentenceLength >= 14 && averageSentenceLength <= 22) {
    score -= 6;
  }

  if (stopWordRatio < 0.38) {
    score += 6;
    insights.push("Lower stop-word usage can indicate highly optimized, machine-generated text.");
  } else if (stopWordRatio > 0.6) {
    score -= 4;
  }

  if (longWordRatio < 0.18) {
    score += 5;
    insights.push("Minimal use of longer words can occur in AI passages kept overly simple.");
  } else if (longWordRatio > 0.3) {
    score -= 3;
  }

  if (topWordShare > 0.05) {
    score += 8;
    insights.push("Several words repeat very frequently, a sign of automated phrasing.");
  } else if (topWordShare < 0.02) {
    score -= 2;
  }

  if (repetitivePhraseRatio > 0.03) {
    score += 7;
    insights.push("We spotted phrases that repeat, which is common in AI-generated drafts.");
  }

  const clampedScore = clamp(Math.round(score), 0, 100);
  const verdict =
    clampedScore >= 70
      ? "High likelihood of AI-generated patterns"
      : clampedScore >= 45
      ? "Mixed signals"
      : "More likely human-written";

  return {
    verdict,
    score: clampedScore,
    metrics: {
      totalWords,
      sentences: sentences.length || 1,
      averageSentenceLength,
      sentenceVariation,
      lexicalRichness,
      stopWordRatio,
      longWordRatio,
      topWordShare,
      repetitivePhraseRatio,
    },
    insights,
    sample: cleaned.split(/\s+/).slice(0, 120).join(" "),
  };
};

function AIDetector() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState("");

  const analyzeUrl = async () => {
    setError("");
    setAnalysis(null);

    let targetUrl = url.trim();
    if (!targetUrl) {
      setError("Enter a URL to start the analysis.");
      return;
    }

    setLoading(true);

    try {
      if (!/^https?:\/\//i.test(targetUrl)) {
        targetUrl = `https://${targetUrl}`;
      }

      const candidates = [targetUrl];
      try {
        const alt = new URL(targetUrl);
        if (alt.hostname.startsWith("www.")) {
          alt.hostname = alt.hostname.replace(/^www\./, "");
        } else {
          alt.hostname = `www.${alt.hostname}`;
        }
        candidates.push(alt.toString());
      } catch (err) {
        // Ignore malformed alternatives
      }

      let success = false;
      for (const candidate of candidates) {
        try {
          const response = await fetch(
            `https://api.allorigins.win/get?url=${encodeURIComponent(candidate)}`
          );

          if (!response.ok) {
            throw new Error("Unable to fetch the page content.");
          }

          const data = await response.json();
          if (!data.contents) {
            throw new Error("No readable content was returned from the requested URL.");
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(data.contents, "text/html");
          const textContent = extractMainContent(doc);

          if (!textContent || textContent.trim().length < 80) {
            throw new Error("We couldn't find enough on-page text to review.");
          }

          const result = analyzeWritingStyle(textContent);
          const title = doc.querySelector("title")?.innerText || "";
          const description = doc.querySelector('meta[name="description"]')?.content || "";

          setAnalysis({
            ...result,
            pageTitle: title,
            description,
            inspectedUrl: candidate,
          });
          success = true;
          break;
        } catch (fetchError) {
          console.warn(`AI detector fetch failed for ${candidate}:`, fetchError);
          continue;
        }
      }

      if (!success) {
        throw new Error("Unable to analyze this URL. Please check the address or try another page.");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred while analyzing the page.");
    } finally {
      setLoading(false);
    }
  };

  const renderMetric = (label, value, hint) => (
    <div className="flex flex-col">
      <span className="text-sm font-semibold text-slate-900">{label}</span>
      <span className="text-lg font-medium text-slate-700">{value}</span>
      {hint && <span className="text-xs text-slate-500 mt-1">{hint}</span>}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>AI Writing Detector - Seotic</title>
        <meta
          name="description"
          content="Review any public URL and get a quick estimate of the likelihood that its content was generated with AI."
        />
      </Helmet>
      <Navbar />
      <div className="px-5 bg-gradient-to-t from-slate-100 to-white">
        <div className="container mx-auto pt-20 pb-16">
          <div className="flex flex-col items-center mb-10 text-center">
            <h2 className="text-base uppercase tracking-widest text-slate-400">PUBLIC TOOL</h2>
        

            <p className="mx-auto mt-2 max-w-xl text-center text-4xl font-semibold tracking-tight text-pretty text-gray-950 sm:text-5xl">
            AI writing detector for live blog posts
            </p>
            <p className="mt-4 max-w-2xl text-slate-600">
              Paste any article orblog post URL to review AI patterns.
            </p>
          </div>

          <div className="flex mb-6 max-w-md mx-auto">
              <input
                id="ai-detector-url"
                type="text"
                placeholder="https://example.com/article"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                className="mr-2 flex-1 px-4 py-3 border border-slate-200 rounded-lg focus:outline-none"
              />
              <button
                onClick={analyzeUrl}
                className="px-5 py-3 bg-yellow-300 text-yellow-900 rounded-lg hover:bg-yellow-400">
                Check
              </button>
            </div>
           
            {loading && <p className="text-center mt-10">Checking...</p>}
          {error && <p className="text-center text-red-500 mt-10">{error}</p>}
          

          {analysis && (
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-1 bg-white border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between px-10 pt-10">
                  <span className="text-sm font-semibold uppercase tracking-widest text-slate-400">Likelihood</span>
                  <span className="text-xs text-slate-500">Score out of 100</span>
                </div>
                <div className="px-10 pt-5">
                  <div className="text-5xl font-bold text-slate-900">{analysis.score}</div>
                  <div className="mt-2 text-lg font-medium text-slate-700">{analysis.verdict}</div>
                </div>
                <div className="px-10 pt-5">
                  <div className="h-2 rounded-full bg-slate-200">
                    <div
                      className={`h-2 rounded-full ${
                        analysis.score >= 70
                          ? "bg-red-400"
                          : analysis.score >= 45
                          ? "bg-yellow-300"
                          : "bg-emerald-400"
                      }`}
                      style={{ width: `${analysis.score}%` }}
                    ></div>
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                  A higher score means the text looks more like it might be written by AI but it’s not proof.</p>
                </div>
                <div className="mt-6 border-t border-slate-200 px-10 pt-5 pb-10 space-y-2">
                  {analysis.inspectedUrl && (
                    <div className="text-xs text-slate-500 break-words">
                      <span className="font-semibold text-slate-700">Analyzed URL:</span> {analysis.inspectedUrl}
                    </div>
                  )}
                  {analysis.pageTitle && (
                    <div className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">Page title:</span> {analysis.pageTitle}
                    </div>
                  )}
                  {analysis.description && (
                    <div className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">Meta description:</span> {analysis.description}
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-10">
                <h3 className="text-lg font-semibold text-slate-900">Writing metrics</h3>
                <p className="mt-1 text-sm text-slate-500">How the page compares to typical human-written articles.</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {renderMetric(
                    "Total words analyzed",
                    analysis.metrics.totalWords.toLocaleString(),
                    "Fewer than ~80 words is usually not enough to judge."
                  )}
                  {renderMetric(
                    "Sentences reviewed",
                    analysis.metrics.sentences.toLocaleString(),
                    "We removed menus / boilerplate before counting."
                  )}
                  {renderMetric(
                    "Average sentence length",
                    `${analysis.metrics.averageSentenceLength.toFixed(1)} words`,
                    "14–22 words is typical for web writing."
                  )}
                  {renderMetric(
                    "Sentence length variation",
                    `${(analysis.metrics.sentenceVariation * 100).toFixed(0)}%`,
                    "More mix of short and long sentences feels more human."
                  )}
                  {renderMetric(
                    "Lexical richness",
                    `${(analysis.metrics.lexicalRichness * 100).toFixed(0)}% unique`,
                    "Higher scores mean more different words used."
                  )}
                  {renderMetric(
                    "Stop-word ratio",
                    `${(analysis.metrics.stopWordRatio * 100).toFixed(0)}%`,
                    "40–60% is common for human writing."
                  )}
                  {renderMetric(
                    "Long word usage",
                    `${(analysis.metrics.longWordRatio * 100).toFixed(0)}%`,
                    "Longer words (>7 letters) can show human-like writing."
                  )}
                  {renderMetric(
                    "Top word repetition",
                    `${(analysis.metrics.topWordShare * 100).toFixed(1)}%`,
                    "Over 5% may mean one word is repeated too much."
                  )}
                  {renderMetric(
                    "Phrase repetition",
                    `${(analysis.metrics.repetitivePhraseRatio * 100).toFixed(1)}%`,
                    "We check for repeated two word phrases."
                  )}
                </div>
              </div>

              

              

              
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="bg-white border border-slate-200 rounded-lg p-10">
              <h3 className="text-lg font-semibold text-slate-900">Why check for AI writing?</h3>
              <p className="mt-2 text-sm text-slate-600">
                AI-written copy can be efficient but you may want to flag sections that need fact checking, tone adjustments or original insights before publishing.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-10">
              <h3 className="text-lg font-semibold text-slate-900">Best results</h3>
              <p className="mt-2 text-sm text-slate-600">
                Works best on evergreen articles, landing pages, documentation and blog posts. Short notices, tables or app dashboards typically lack enough narrative content to score reliably.
              </p>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-10">
              <h3 className="text-lg font-semibold text-slate-900">Protect privacy</h3>
              <p className="mt-2 text-sm text-slate-600">
                We do not store URLs or results. Analyses run in your browser using a public proxy, so avoid pasting private or internal addresses.
              </p>
            </div>
          </div>
        </div>
      </div>
      <InBlog />
      <Faq />
      <div className="container mx-auto my-10">
        <ChatButton />
      </div>

      <Footer />
    </>
  );
}

export default AIDetector;